<template>
    <div class="device-list">
        <h5 class="device-list__title">{{ $t('YOUR DEVICES') }}</h5>
        <list-group-skeleton v-if="loading" :rows="3" />
        <list v-else>
            <list-item v-for="auth in auths" :key="auth.name">
                <device-item ref="deviceItem" :auth="auth" @deleteApp="openDeleteDeviceModal" />
            </list-item>
            <list-item v-if="auths.length === 0">
                <p class="web-auth-card__empty-message text-muted">
                    <span class="fas fa-info-circle"></span>
                    {{ $t('No authentication devices have been linked to this method yet') }}
                </p>
            </list-item>
        </list>
        <delete-device-modal ref="DeleteDeviceModal" @appDeleted="appDeleted"/>
    </div>
</template>


<script>
import base64url from 'base64url';

import List from '@/components/ui/list/List';
import ListItem from '@/components/ui/list/ListItem';
import DeviceItem from '@/components/authentication/webauthn/DeviceItem.vue';
import ListGroupSkeleton from '@/components/ui/skeletons/ListGroupSkeleton.vue';
import DeleteDeviceModal from '@/components/authentication/webauthn/DeleteDeviceModal.vue';

export default {
    name: 'DeviceList',
    components: {
        List,
        ListItem,
        DeviceItem,
        DeleteDeviceModal,
        ListGroupSkeleton,
    },
    data() {
        return {
            loading: false,
            auths: [],
            newDevice: null,
            saving: false,
        };
    },
    mounted() {
        this.getAll();
    },
    watch: {
        newDevice(newVal) {
            if (!newVal) return;            
            if (!newVal._id && !newVal.name) {
                this.getAll();
                return;
            }
            
            if(!this.auths.length) {
                this.auths.push(newVal);
                this.$toasted.global.successMessage(this.$t('Device successfully registered'));
                return;
            }

            let devices_name = this.auths?.map(item => item.name);

            if(!devices_name.length){
                this.auths.push(newVal);
                this.$toasted.global.successMessage(this.$t('Device successfully registered'));
            }
            if(!devices_name.includes(newVal.name)) {
                this.auths.push(newVal);
                this.$toasted.global.successMessage(this.$t('Device successfully registered'));
            } else {
                // Actualizar el dispositivo existente en lugar de agregar uno nuevo
                const index = this.auths.findIndex(item => item.name === newVal.name);
                if (index !== -1) {
                    this.$set(this.auths, index, newVal);
                }
                this.$toasted.global.successMessage(this.$t('Device successfully updated')); 
            }
        }
    },
    methods: {
        openDeleteDeviceModal(app) {
            this.$refs.DeleteDeviceModal.openModal(app);
        },
        appDeleted(app) {
            const search_index = this.auths.findIndex(item => item.name === app.name);
            this.auths.splice(search_index, 1);
        },
        async getAll() {
            try {
                this.loading = true;
                const { data } = await this.axiosAccount.get('/api/biometric-auth');
                this.auths = data.docs;
            } catch (err) {
                this.$toasted.global.errorMessage('Failed to get devices.', err);
            } finally {
                this.loading = false;
            }
        },
        base64urlToUnit8Array(base64urlString) {
            const base64String = base64urlString.replace(/-/g, '+').replace(/_/g, '/');
            const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
            const base64Padded = base64String + padding;
            const binaryString = window.atob(base64Padded);
            const byteArray = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
            }
            return byteArray;
        },
        format_credential(credential) {
            return {
                clientDataJSON: base64url.encode(credential.response.clientDataJSON),
                attestationObject: base64url.encode(credential.response.attestationObject),
                transports: credential.response.getTransports()            
            };
        },
        async getCredentialOptions() {
            try {
                const { data } = await this.axiosAccount.get('/api/biometric-auth/register/options');
                let { options } = data;
                const challenge = this.base64urlToUnit8Array(options.challenge);
                options.challenge = challenge;
                const user = this.base64urlToUnit8Array(options.user.id);
                options.user.id = user;
                const excludeCredentials = options.excludeCredentials.map(cred => {
                    return {
                        ...cred,
                        id: new Uint8Array(base64url.toBuffer(cred.id))
                    };
                });
                options.excludeCredentials = excludeCredentials;
                return options;
            } catch (error) {
                throw error;
            }
        },
        async getCredential(options) {
            try {
                const credential = await navigator.credentials.create({
                    publicKey: options
                });
                return credential;
            } catch (error) {
                throw error;
            }
        },
        async addDevice() {
            try {
                this.saving = true;
                this.$emit('saving-changed', true);
                const options = await this.getCredentialOptions();
                const credential = await this.getCredential(options);
                const payload = {
                    id: base64url.encode(credential.rawId),
                    credential: this.format_credential(credential),
                    attachment: credential.authenticatorAttachment,
                    extensions: credential.getClientExtensionResults(),
                    type: credential.type
                };
                const response = await this.axiosAccount.post('/api/biometric-auth/register', payload);
                const device = response.data.doc || response.data;
                if (device) {
                    this.newDevice = device;
                } else {
                    await this.getAll();
                    this.$toasted.global.successMessage(this.$t('Device successfully registered'));
                }
            } catch (error) {
                this.$toasted.global.errorMessage('We could not create the passkey. Please try again later.');
            } finally {
                this.saving = false;
                this.$emit('saving-changed', false);
            }
        }
    }
};
</script>

<style lang="scss">
.device-list {
    &__title {
        color: $general-black;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 1rem;
        text-align: left;
    }
}
</style>